import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI, boardsAPI, authAPI } from '../../api';
import { getTranslation } from '../../i18n';
import ClientInfoCard from './components/ClientInfoCard';
import HistorySection from './components/HistorySection';
import ClientDocuments from './components/ClientDocuments';
import ClientBoards from './components/ClientBoards';

export default function ClientSpace() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [boards, setBoards] = useState([]);
  const [allBoards, setAllBoards] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tab, setTab] = useState('history');
  const [lang, setLang] = useState('en');
  const [toast, setToast] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    authAPI.getSettings().then(({ data }) => setLang(data.language || 'en')).catch(() => {});
    fetchClient();
    fetchBoards();
    fetchDocuments();
    boardsAPI.list().then(({ data }) => setAllBoards(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const t = getTranslation(lang);
  document.title = client ? `Maestra - ${client.name}` : 'Maestra - Client Space';

  const fetchClient = async () => {
    try {
      const { data } = await clientsAPI.get(clientId);
      setClient(data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
    }
  };
  const fetchBoards = () => clientsAPI.boards.list(clientId).then(({ data }) => setBoards(data)).catch(() => {});
  const fetchDocuments = () => clientsAPI.documents.list(clientId).then(({ data }) => setDocuments(data)).catch(() => {});

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const handleExportArchive = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await clientsAPI.exportClientZip(clientId);
      const res = await clientsAPI.downloadExport(data.download_token);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || 'Client_Archive.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      showToast(t.cs_export_failed || 'Export failed — please try again.');
    } finally { setExporting(false); }
  };

  if (notFound) {
    return (
      <div className="dashboard fade-in" style={{ textAlign: 'center', paddingTop: 120 }}>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 16, color: 'var(--kahla-text-light)' }}>
          {t.cs_not_found || 'Client not found.'}
        </p>
        <button className="btn-ghost" onClick={() => navigate('/clients')}>← {t.cs_back || 'Clients'}</button>
      </div>
    );
  }

  if (!client) {
    return <div className="dashboard" style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}><div className="spinner" /></div>;
  }

  const TABS = [
    { key: 'history', label: t.cs_tab_history || 'History' },
    { key: 'documents', label: t.cs_tab_documents || 'Documents' },
  ];

  return (
    <div className="dashboard fade-in" data-testid="client-space-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 34 }}>
        <nav style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <button
            type="button"
            data-testid="client-back-btn"
            onClick={() => navigate('/clients')}
            style={{
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              fontFamily: 'Playfair Display, serif', fontSize: 11, letterSpacing: '0.24em',
              textTransform: 'uppercase', color: 'var(--kahla-text-light)',
            }}
          >{t.cs_title || 'Client Space'}</button>
          <span style={{ color: '#c9c1b2', fontSize: 11 }}>/</span>
          <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--kahla-charcoal)' }}>
            {client.name}
          </span>
        </nav>
        <button
          type="button" className="btn-ghost" data-testid="export-client-archive-btn"
          onClick={handleExportArchive} disabled={exporting}
          style={{ fontSize: 11 }}
        >{exporting ? (t.cs_exporting || 'Preparing…') : (t.cs_export_client || 'Download archive')}</button>
      </div>

      <ClientInfoCard
        client={client}
        t={t}
        lang={lang}
        onUpdated={(c) => setClient(c)}
        onDeleted={() => navigate('/clients')}
        showToast={showToast}
      />

      <div className="client-columns" style={{ marginTop: 44 }}>
        <div style={{ minWidth: 0 }}>
          <nav data-testid="client-tabs" style={{ display: 'flex', gap: 26, margin: '0 0 30px', borderBottom: '1px solid var(--kahla-border)', paddingBottom: 0 }}>
            {TABS.map(({ key, label }) => {
              const selected = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`client-tab-${key}`}
                  onClick={() => setTab(key)}
                  aria-pressed={selected}
                  style={{
                    background: 'transparent', border: 'none', padding: '0 0 12px', cursor: 'pointer',
                    fontFamily: 'Playfair Display, serif', fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase',
                    color: selected ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                    borderBottom: selected ? '1px solid var(--kahla-charcoal)' : '1px solid transparent',
                    marginBottom: -1, transition: 'color 200ms ease, border-color 200ms ease',
                  }}
                >{label}</button>
              );
            })}
          </nav>

          {tab === 'history' && (
            <HistorySection clientId={clientId} t={t} lang={lang} boards={allBoards} documents={documents} showToast={showToast} />
          )}
          {tab === 'documents' && (
            <ClientDocuments clientId={clientId} t={t} lang={lang} documents={documents} allBoards={allBoards} refresh={fetchDocuments} showToast={showToast} />
          )}
        </div>

        <ClientBoards
          clientId={clientId}
          client={client}
          t={t}
          lang={lang}
          boards={boards}
          allBoards={allBoards}
          refresh={() => { fetchBoards(); boardsAPI.list().then(({ data }) => setAllBoards(data)).catch(() => {}); }}
          showToast={showToast}
        />
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
